import {Col, Form, Row, Tooltip} from 'antd';
import {observer} from 'mobx-react';
import React from 'react';
import styled from 'styled-components';
import {Button} from '@/components/common-components/v2';
import {useStore} from '@/store/root-store';
import {snakeToCamel} from '@/utils/string';
import OttoKnowledgeGraphFields from './ottoKnowledgeGraphFields';
import {languages} from '../../../landing-page-optimizer/page-editor/Drawers/AiContentToolsDrawer/languages';
import {URL_PATTERN} from '@/constants/regex';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCircleInfo} from '@fortawesome/pro-regular-svg-icons';

const excludedLanguages = ['None', 'jv', 'tt', 'rw', 'tr'];

export const fieldOptionsMapping = {
  language_code: languages.map(lng => ({label: lng.language, value: lng.code})).filter(lng => !excludedLanguages.includes(lng.value)),
};
export const fieldTypeMapping = {
  lat: 'number',
  long: 'number',
};

export const BusinessInfoForm = observer(({
  data,
  serpLocation,
  setSerpLocation,
  setSerpLocationId,
  form,
  style = {},
  gutter = null,
  span = null,
  labelSpan = null,
  maxHeight = '600px',
  formErrorMargin = null,
  languageCode,
  setLanguageCode,
  dropDownBottomValue = null,
  passedValue=null,
  markEmptyFields = false,
  showFieldsCompletion = false,
}) => {
  const {
    ottoV2Store: {knowledgeGraph},
  } = useStore('');


  const addressFields = ['street_address', 'address_locality', 'address_administrative_area', 'address_country', 'address_region'];
  return (
    <>
      <FieldsContainer style={style} maxHeight={maxHeight}>
        {data?.filter(item => item.name !== 'logo' && item.name !== 'image')?.map((item, index) => {
          const defaultValue = knowledgeGraph[item?.name === 'iso_6523_code' ? 'iso6523Code' : snakeToCamel(item.name)];
          return (
            <StyledFormItem
              name={item.name}
              key={index}
              rules={[
                item.validation,
                item.type === 'url' && {
                  type: 'url',
                  message: `Must be a valid ${item.name.replace('_url', '')} URL`,
                  validator: async (_, value) => {
                    if (!value) return Promise.resolve();

                    if (URL_PATTERN[item.name]) {
                      if (!URL_PATTERN[item.name].test(value)) {
                        return Promise.reject(new Error(`Please enter a valid ${item.name.replace('_url', '')} URL`));
                      }
                    } else {
                      const generalUrlPattern = /^https?:\/\/(www\.)?[\w.-]+\/?$/;
                      if (!generalUrlPattern.test(value)) {
                        return Promise.reject(new Error(`Please enter a valid ${item.name.replace('_url', '')} URL`));
                      }
                    }
                  },
                },
                item?.validator,
              ].filter(rule => rule)}
              formErrorMargin={formErrorMargin}
            >
              <Row style={{marginTop: '6px'}} className='input-wrapper' {...(gutter && {gutter})}>
                <Col lg={labelSpan || 6} className='label' span={labelSpan}>{item.label}
                  {addressFields?.includes(item?.name) && <Tooltip title={'Multiple business locations? Please add the Google Business Profile for each location  instead of completing this field.'} overlayStyle={{maxWidth: 400, zIndex: 1200}} overlayInnerStyle={{backgroundColor: '#000', borderRadius: 6}}>
                    <FontAwesomeIcon icon={faCircleInfo} style={{cursor: 'pointer', marginLeft: '3px', position: 'relative', top: 0.5}} color='#4E5156'/>
                  </Tooltip>}
                </Col>

                <Col lg={span || 18}>
                  <OttoKnowledgeGraphFields
                    dropDownBottomValue={dropDownBottomValue}
                    placeholder={item.label}
                    defaultValue={defaultValue}
                    fieldType={fieldTypeMapping[item?.name] || item?.type}
                    name={item.name}
                    options={fieldOptionsMapping[item?.name] || item?.options}
                    form={form}
                    serpLocation={serpLocation}
                    setSerpLocation={setSerpLocation}
                    setSerpLocationId={setSerpLocationId}
                    languageCode={languageCode}
                    setLanguageCode={setLanguageCode}
                    disabled={item.disabled}
                    markEmptyFields={markEmptyFields}
                    showFieldsCompletion={showFieldsCompletion}
                    {...((passedValue && item?.name === 'website')) && {passedValue: passedValue}}
                  />
                </Col>
              </Row>
            </StyledFormItem>
          );
        })}
      </FieldsContainer>
    </>
  );
});

const StyledFormItem = styled(Form.Item)<{formErrorMargin?: string}>`
  margin-bottom: 0px !important;
  .ant-form-item-explain-error {
    margin-left: ${p => p?.formErrorMargin ? p?.formErrorMargin : '25%'};
    margin-top: -8px;
    font-size: 12px;
  }
  .ant-form-item {
    margin-bottom: 0px !important;
  }
`;

export const StyledButton = styled(Button)`
  border-radius: 8px !important;
`;

const FieldsContainer = styled.div<{maxHeight?: string}>`
 padding-right: 5px;
 max-height: ${p => p.maxHeight};
 overflow: auto;

 ::-webkit-scrollbar {
   width: 5px;
   height: 5px;
 }

::-webkit-scrollbar-track {
  background: #F9F9FB;
 }

::-webkit-scrollbar-thumb {
   background: #4E515680;
   border-radius: 5px;
 }
`;
